import {
  ICTTextCharacterProperties,
  ICTTextParagraph,
  ICTTextParagraphProperties,
  IEGTextRun,
  STTextStrikeType,
} from '@tencent/slide-sdk-model';
import { cloneDeep } from 'lodash';
import { marked } from 'marked';
import { Token, InlineToken } from '../define/token';
import { removePrefix } from '../utils/text-utils';
import { unescapeHTML } from '../utils/utils';

export const fillTextRun = (text: string, textRun: IEGTextRun): IEGTextRun => {
  const txRun = cloneDeep(textRun);
  if (!txRun.r) {
    txRun.r = {};
  }
  txRun.r.t = removePrefix(text.replace('\n', ' '));
  return txRun;
};

export const createParagraphPropsForList = (level = 0): ICTTextParagraphProperties => {
  const maxLevel = 3;
  const FONT_SIZE = 16 * 12700;
  const indent = -FONT_SIZE * 1;
  const marginLeft = FONT_SIZE * level + (level ? -indent : 0);
  const l = Math.min(maxLevel, level);
  return {
    lvl: l,
    marL: marginLeft,
    indent,
    buFont: {
      typeface: 'Arial',
      panose: '020B0604020202020204',
      pitchFamily: 34,
      charset: 0,
    },
    buChar: {
      charKeyword: '•',
    },
  };
};

/** Tokens.List => 带项目符号的文本  */
export const renderList = (
  token: marked.Tokens.List,
  paragraphList: ICTTextParagraph[],
  subType: 'title' | 'content' = 'content',
  level = 0,
): ICTTextParagraph[] => {
  const resultParagraphList: ICTTextParagraph[] = [];
  // 第一级列表如果只有一项，就显示为普通文本（即不带项目符号）
  const asNormalText = level === 0 && token.items.length === 1;

  token.items.forEach((listItem) => {
    if (!listItem.tokens) return;
    const pPr = paragraphList[0].pPr ?? {};
    listItem.tokens.forEach((token) => {
      if (token.type === 'text' && 'tokens' in token && token.tokens) {
        let newPr: ICTTextParagraphProperties = {
          ...pPr,
          marL: 0,
          indent: 0,
          buNone: true,
          lnSpc: {
            spcPct: {
              // 标题默认 1 倍行距，内容 1.3 倍
              val: subType === 'title' ? 100000 : 130000,
            },
          },
        };
        if (!asNormalText) {
          newPr = { ...newPr, ...createParagraphPropsForList(level) };
        }

        const paragraph: ICTTextParagraph = {
          pPr: newPr,
          egTextRun: renderTextRunList(token as marked.Tokens.Text, paragraphList[0].egTextRun![0]),
        };
        resultParagraphList.push(paragraph);
        return;
      }

      if (token.type === 'list') {
        resultParagraphList.push(...renderList(token as marked.Tokens.List, paragraphList, subType, level + 1));
        return;
      }
      // bad case;
    });
  });

  return resultParagraphList;
};

const getTextPropsFromToken = (token: InlineToken): ICTTextCharacterProperties | undefined => {
  switch (token.type) {
    case 'text':
    case 'codespan':
    case 'escape':
    case 'link':
      return {};
    // return { link: { url: token.href } };
    case 'strong':
      return { b: true };
    case 'del':
      return { strike: STTextStrikeType.sngStrike };
    case 'em':
      return { i: true };
    default:
      console.error('not support', JSON.stringify(token));
      break;
  }
};

// TODO: inline 可以嵌套, 比如下面这个 badcase
// **This is bold text ~~test~~**
const renderRun = (token: InlineToken, textRun: IEGTextRun): IEGTextRun | IEGTextRun[] => {
  if (token.type === 'image') return [];

  textRun = cloneDeep(textRun);
  if (textRun.r) {
    textRun.r.rPr = {
      ...textRun.r.rPr,
      ...getTextPropsFromToken(token),
    };

    // 如果使用了主题色，统一去除 colorTransform
    if (textRun.r?.rPr?.solidFill?.egColorChoice?.schemeClr?.egColorTransform) {
      delete textRun.r?.rPr?.solidFill?.egColorChoice?.schemeClr?.egColorTransform;
    }
  }

  if ('tokens' in token && token.tokens?.length) {
    return token.tokens.flatMap((t) => renderRun(t as InlineToken, textRun));
  }

  return fillTextRun(unescapeHTML(token.text), textRun);
};

const renderTextRunList = (token: Token, textRun: IEGTextRun) => {
  if (!('tokens' in token) || !token.tokens) return [];

  return token.tokens.flatMap((token) => renderRun(token as InlineToken, textRun));
};

export const renderParagraphList = (
  tokens: Token | Token[],
  paragraphList: ICTTextParagraph[],
  subType: 'title' | 'content' = 'content',
): ICTTextParagraph[] => {
  const tokenList = Array.isArray(tokens) ? tokens : [tokens];
  return tokenList
    .flatMap((token) => {
      if (token.type === 'list') return renderList(token, paragraphList, subType);

      const [p] = paragraphList;
      return {
        ...p,
        pPr: {
          ...p.pPr,
          marL: 0,
          indent: 0,
          buNone: true,
          lnSpc: {
            spcPct: {
              // 标题默认 1 倍行距，内容 1.3 倍
              val: subType === 'title' ? 100000 : 130000,
            },
          },
        },
        egTextRun: renderTextRunList(token, p.egTextRun![0]),
      };
    })
    .filter((p) => !!p);
};
